import axios from 'axios';
import { ApiResponse, User, Category, Layer, POI, UploadResponse } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email: string, password: string, recaptchaToken?: string | null) => {
    const payload: any = { email, password };
    if (recaptchaToken) {
      payload.recaptchaToken = recaptchaToken;
    }
    const response = await api.post('/auth/login', payload);
    return response.data;
  },

  getMe: async (): Promise<ApiResponse<User>> => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  updatePassword: async (currentPassword: string, newPassword: string) => {
    const response = await api.put('/auth/updatepassword', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },

  forgotPassword: async (email: string) => {
    const response = await api.post('/auth/forgotpassword', { email });
    return response.data;
  },

  resetPassword: async (resetToken: string, password: string) => {
    const response = await api.put(`/auth/resetpassword/${resetToken}`, {
      password,
    });
    return response.data;
  },

  getRecaptchaConfig: async () => {
    const response = await api.get('/auth/recaptcha-config');
    return response.data;
  },
};

// Categories API
export const categoriesAPI = {
  getAll: async (params?: { isActive?: boolean }): Promise<ApiResponse<Category[]>> => {
    const response = await api.get('/categories', { params });
    return response.data;
  },

  getById: async (id: string): Promise<ApiResponse<Category>> => {
    const response = await api.get(`/categories/${id}`);
    return response.data;
  },

  create: async (data: Partial<Category>): Promise<ApiResponse<Category>> => {
    const response = await api.post('/categories', data);
    return response.data;
  },

  update: async (id: string, data: Partial<Category>): Promise<ApiResponse<Category>> => {
    const response = await api.put(`/categories/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/categories/${id}`);
    return response.data;
  },

  reorder: async (categories: { id: string }[]): Promise<ApiResponse<Category[]>> => {
    const response = await api.put('/categories/bulk/reorder', { categories });
    return response.data;
  },
};

// Layers API
export const layersAPI = {
  getAll: async (params?: { isActive?: boolean }): Promise<ApiResponse<Layer[]>> => {
    const response = await api.get('/layers', { params });
    return response.data;
  },

  getById: async (id: string): Promise<ApiResponse<Layer>> => {
    const response = await api.get(`/layers/${id}`);
    return response.data;
  },

  getByCode: async (code: string): Promise<ApiResponse<Layer>> => {
    const response = await api.get(`/layers/code/${code}`);
    return response.data;
  },

  create: async (data: Partial<Layer>): Promise<ApiResponse<Layer>> => {
    const response = await api.post('/layers', data);
    return response.data;
  },

  update: async (id: string, data: Partial<Layer>): Promise<ApiResponse<Layer>> => {
    const response = await api.put(`/layers/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/layers/${id}`);
    return response.data;
  },

  reorder: async (layers: { id: string }[]): Promise<ApiResponse<Layer[]>> => {
    const response = await api.put('/layers/bulk/reorder', { layers });
    return response.data;
  },
};

// POIs API
export const poisAPI = {
  getAll: async (params?: {
    category?: string;
    layer?: string;
    isActive?: boolean;
    isFeatured?: boolean;
    search?: string;
    lat?: number;
    lng?: number;
    radius?: number;
    page?: number;
    limit?: number;
    sort?: string;
  }): Promise<ApiResponse<POI[]>> => {
    const response = await api.get('/pois', { params });
    return response.data;
  },

  getById: async (id: string): Promise<ApiResponse<POI>> => {
    const response = await api.get(`/pois/${id}`);
    return response.data;
  },

  create: async (data: Partial<POI>): Promise<ApiResponse<POI>> => {
    const response = await api.post('/pois', data);
    return response.data;
  },

  update: async (id: string, data: Partial<POI>): Promise<ApiResponse<POI>> => {
    const response = await api.put(`/pois/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/pois/${id}`);
    return response.data;
  },

  addImages: async (id: string, images: any[]): Promise<ApiResponse<POI>> => {
    const response = await api.post(`/pois/${id}/images`, { images });
    return response.data;
  },

  removeImage: async (id: string, imageId: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/pois/${id}/images/${imageId}`);
    return response.data;
  },

  submitRating: async (id: string, rating: number): Promise<ApiResponse<{ rating: number; reviewCount: number; poi: POI }>> => {
    const response = await api.post(`/pois/${id}/rate`, { rating });
    return response.data;
  },
};

// Upload API
export const uploadAPI = {
  single: async (file: File): Promise<ApiResponse<UploadResponse>> => {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await api.post('/upload/single', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  multiple: async (files: File[]): Promise<ApiResponse<UploadResponse[]>> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('images', file));
    
    const response = await api.post('/upload/multiple', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  withMetadata: async (
    file: File,
    metadata: { caption?: string; isPrimary?: boolean }
  ): Promise<ApiResponse<UploadResponse>> => {
    const formData = new FormData();
    formData.append('image', file);
    if (metadata.caption) formData.append('caption', metadata.caption);
    if (metadata.isPrimary !== undefined) {
      formData.append('isPrimary', metadata.isPrimary.toString());
    }
    
    const response = await api.post('/upload/with-metadata', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Seed API
export const seedAPI = {
  createAdmin: async () => {
    const response = await api.post('/seed/admin');
    return response.data;
  },

  seedCategories: async () => {
    const response = await api.post('/seed/categories');
    return response.data;
  },

  seedLayers: async () => {
    const response = await api.post('/seed/layers');
    return response.data;
  },

  seedPOIs: async () => {
    const response = await api.post('/seed/pois');
    return response.data;
  },

  seedAll: async () => {
    const response = await api.post('/seed/all');
    return response.data;
  },
};

// Excel API
export const excelAPI = {
  downloadTemplate: async () => {
    const response = await api.get('/excel/template', {
      responseType: 'blob'
    });
    return response;
  },

  exportPOIs: async () => {
    const response = await api.get('/excel/export', {
      responseType: 'blob'
    });
    return response;
  },

  importPOIs: async (file: File): Promise<ApiResponse<{
    total: number;
    created: number;
    updated: number;
    errors: string[];
  }>> => {
    const formData = new FormData();
    formData.append('excel', file);
    
    const response = await api.post('/excel/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// KML API
export const kmlAPI = {
  getFiles: async (): Promise<ApiResponse<{
    filename: string;
    originalName: string;
    size: number;
    uploadDate: string;
    url: string;
  }[]>> => {
    const response = await api.get('/kml/files');
    return response.data;
  },

  uploadFile: async (file: File): Promise<ApiResponse<{
    filename: string;
    originalName: string;
    size: number;
    uploadDate: string;
    url: string;
  }>> => {
    const formData = new FormData();
    formData.append('kml', file);
    
    const response = await api.post('/kml/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteFile: async (filename: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/kml/files/${filename}`);
    return response.data;
  },

  getFileContent: async (filename: string): Promise<ApiResponse<{
    filename: string;
    content: string;
  }>> => {
    const response = await api.get(`/kml/files/${filename}/content`);
    return response.data;
  },
};

// Translations API
// Contact API
export const contactAPI = {
  // Submit contact form
  submit: async (data: {
    fullName: string;
    email: string;
    phone: string;
    company?: string;
    position?: string;
    message: string;
  }): Promise<ApiResponse<{ id: string; submittedAt: string }>> => {
    const response = await api.post('/contacts/submit', data);
    return response.data;
  },

  // Get contact info
  getInfo: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/contacts/info');
    return response.data;
  },

  // Admin endpoints
  getSubmissions: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<{
    submissions: any[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  }>> => {
    const response = await api.get('/contacts/submissions', { params });
    return response.data;
  },

  // Get submission statistics
  getStats: async (): Promise<ApiResponse<{
    total: number;
    unread: number;
    new: number;
    byStatus: Record<string, number>;
  }>> => {
    const response = await api.get('/contacts/submissions/stats');
    return response.data;
  },

  // Get specific submission
  getSubmission: async (id: string): Promise<ApiResponse<any>> => {
    const response = await api.get(`/contacts/submissions/${id}`);
    return response.data;
  },

  // Update submission
  updateSubmission: async (id: string, data: {
    status?: string;
    notes?: string;
    assignedTo?: string;
    priority?: string;
  }): Promise<ApiResponse<any>> => {
    const response = await api.put(`/contacts/submissions/${id}`, data);
    return response.data;
  },

  // Delete submission
  deleteSubmission: async (id: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/contacts/submissions/${id}`);
    return response.data;
  },

  // Update contact info
  updateInfo: async (data: any): Promise<ApiResponse<any>> => {
    const response = await api.put('/contacts/info', data);
    return response.data;
  },
};

export const translationsAPI = {
  // Get translations for a specific language (for frontend)
  getTranslations: async (language: 'en' | 'id', category?: string): Promise<ApiResponse<Record<string, string>>> => {
    const response = await api.get(`/translations/${language}`, {
      params: category ? { category } : undefined
    });
    return response.data;
  },

  // Get all translations with pagination (for admin)
  getAdminTranslations: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    category?: string;
  }): Promise<ApiResponse<{
    data: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }>> => {
    const response = await api.get('/translations/admin/all', { params });
    return response.data;
  },

  // Get translation categories
  getCategories: async (): Promise<ApiResponse<string[]>> => {
    const response = await api.get('/translations/admin/categories');
    return response.data;
  },

  // Create translation
  createTranslation: async (data: {
    key: string;
    category: string;
    en: string;
    id: string;
    description?: string;
  }): Promise<ApiResponse<any>> => {
    const response = await api.post('/translations', data);
    return response.data;
  },

  // Update translation
  updateTranslation: async (id: string, data: {
    key?: string;
    category?: string;
    en?: string;
    id?: string;
    description?: string;
  }): Promise<ApiResponse<any>> => {
    const response = await api.put(`/translations/${id}`, data);
    return response.data;
  },

  // Delete translation
  deleteTranslation: async (id: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/translations/${id}`);
    return response.data;
  },

  // User language preferences
  getUserPreference: async (sessionId: string): Promise<ApiResponse<{
    language: string;
    sessionId: string;
  }>> => {
    const response = await api.get(`/translations/preferences/${sessionId}`);
    return response.data;
  },

  // Update user language preference
  updateUserPreference: async (sessionId: string, language: 'en' | 'id'): Promise<ApiResponse<{
    language: string;
    sessionId: string;
  }>> => {
    const response = await api.put(`/translations/preferences/${sessionId}`, { language });
    return response.data;
  },
};

export default api;
